// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import {
  Card,
  CardMedia,
  CardHeader,
  Divider,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Button,
  CardActions,
  Link
} from '@mui/material';
import { useNavigate } from 'react-router-dom'; // Adjust based on your routing setup
import CertificationIcon from "../../assets/img/card-img-2.svg";
import { CERT_APP_REDIRECT_URL } from "../../config/constant";

const CertificationCard = ({
  auth = { isAuthenticated: false },
  landingStats = { backgroundProcess: false, response: { totalCertifications: 0 } },
  contactDetails = {},
  routes = { certifications: '/' },
  authContext = { appSignIn: () => {} },
}) => {
  const navigate = useNavigate();

  // Show the last 3 years with published "Certified Professional of the Year" pages.
  // In Jan-Feb, use currentYear - 2 because the previous year's winners page may not be published yet;
  // from March onward, use currentYear - 1.
  const getCertificationYears = (yearsToShow: number = 3) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    let endYear: number;

    if (currentMonth <= 2) {
      endYear = currentYear - 2; // Jan, Feb
    } else {
      endYear = currentYear - 1; // March onwards
    }

    return Array.from({ length: yearsToShow }, (_, i) => endYear - i);
  };

  const baseUrlWso2CertifiedProfessionals =
  "https://wso2.com/training/certification/certified-professional-of-the-year";
  const certificationYears = getCertificationYears();

  return (
    <Card
      sx={{
        p: 3,
        mb: 3,
        minHeight: 450,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}
    >
      <CardMedia component="img" sx={{ width: 100, ml: 2 }} image={CertificationIcon} />
      <CardHeader
        title="Certifications"
        subheader=""
        titleTypographyProps={{
          align: 'left',
        }}
        subheaderTypographyProps={{
          align: 'right',
        }}
      />
      <Divider variant="middle" />
      <CardContent>
        <Box
          sx={{
            mb: 2,
            height: 124
          }}
        >
          {auth.isAuthenticated ? (
            <>
              {landingStats.backgroundProcess ? (
                <CircularProgress size={30} sx={{ color: 'grey.500' }} />
              ) : (
                <Typography
                  component="h2"
                  variant="h3"
                  color="text.primary"
                >
                  {landingStats.response.totalCertifications}
                </Typography>
              )}
              <Typography color="text.secondary">
                Completed Certifications
              </Typography>
            </>
          ) : (
            <>
              <Typography
                align="left"
                sx={{ mt: 4, mb: 2, minHeight: 32, display: 'flex', alignItems: 'center' }}
              >
                WSO2 Certified Professionals of the Year
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "center", gap: 0, alignItems: "center" }}
              >
                {certificationYears.map((year, index) => (
                  <Box key={year} sx={{ display: "flex", alignItems: "center" }}>
                    <Link
                      href={`${baseUrlWso2CertifiedProfessionals}-${year}/winners/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        fontWeight: "bold",
                        fontSize: "1.1rem",
                        cursor: "pointer",
                        color: "inherit",
                        "&:hover": {
                          color: "primary.main",
                        },
                      }}
                    >
                      {year}
                    </Link>
                    {index < certificationYears.length - 1 && (
                      <Typography
                        sx={{
                          mx: 1.5,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          color: "secondary.main",
                        }}
                      >
                        |
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>

      </CardContent>
      <CardActions>
        <Button
          onClick={() => {
            if (auth.isAuthenticated) {
              navigate(routes.certifications);
            } else {
              localStorage.setItem(
                CERT_APP_REDIRECT_URL,
                routes.certifications
              );
              authContext.appSignIn();
            }
          }}
          variant="contained"
          sx={{ width: { xs: '80%', md: '100%' } }}
        >
          View Certifications
        </Button>
      </CardActions>
    </Card>
  );
};

export default CertificationCard;
